<template>
  <tabs />
  <toolbar class="toolbar">
    <filter-pane v-model:page="page" v-model="filter" :filters="filters" />
    <add-fields-pane v-model="visibleColumns" :fields="columns" />
    <export
      url="/campaign_surplus/csv"
      :filter="totalFilter"
      :columns="columns"
      :visible-columns="visibleColumns"
      :title="csvTitle"
      :sort-key="sortKey"
      :sort-direction="sortDirection"
    />
  </toolbar>
  <div class="main-area h-full">
    <div class="ml-12">
      <CampaignSurplusSummaryStatisticsTable
        :provider-params="providerParams"
      />
      <div class="table">
        <data-table
          class="mb-auto"
          v-model:sort-by="sortKey"
          v-model:sort-direction="sortDirection"
          :busy="isLoading"
          :items="records"
          :columns="columns"
          :visible-columns="visibleColumns"
        />
      </div>
    </div>
  </div>
  <pagination v-model:page="page" v-model:per-page="perPage" class="pagination" :total="total" />
</template>

<script lang="tsx" setup>
import {computed, inject, reactive, ref, type Ref, unref, watch} from 'vue';
import {useI18n} from 'vue-i18n';
import FilterPane from '@components/utils/toolbar/panes/Filter.vue';
import AddFieldsPane from '@components/utils/toolbar/panes/AddFields.vue';
import Toolbar from '@components/utils/toolbar/Toolbar.vue';
import Pagination from '@components/utils/table/Pagination.vue';
import DataTable, {type Column} from '@components/utils/table/Table.vue';
import { collOptionsProvider, useProvider } from '@components/utils/table/hooks/provider';
import type {Filter} from '@utils/filter/Filter.vue';
import type { CampaignGroup, HydraCollection, Region, StatisticPoint } from '@/entities/Entities';
import Tabs from '@components/structure/navigation/Tabs.vue';

import Export from '@utils/toolbar/panes/Export.vue';
import {format} from 'date-fns';
import {useQueryRef} from '@/hooks/url';
import {useTableFields} from '@hooks/table-configuration';
import globalFilterKey from '@utils/globalFilter/injects';
import CampaignSurplusSummaryStatisticsTable
  from '@utils/CampaignSurplusSummaryStatisticsTable.vue';

interface Props {
  modelValue: CampaignGroup;
}

const props = defineProps<Props>();

const campaignGroupNeedsLoading = computed(() => {
  return !props.modelValue;
});

const globalFilter = inject(globalFilterKey) as unknown as Ref<{ regions: {label: string, value: string}[] }>;

const page = useQueryRef('page', 1);
const perPage = useQueryRef('perPage', 5);

const sortKey = useQueryRef<string | undefined>('sortKey', 'computedWeek');
const sortDirection = useQueryRef<'ASC' | 'DESC' | undefined>('sortDirection', 'ASC');

const { t } = useI18n();

const csvTitle = computed(() => {
  return t('csv.title.surplusFromGroup', {
    group: props.modelValue?.name,
  });
});

const filter = useQueryRef('filter', {});
const fixedFilter = computed(() => ({
  'campaign.group': props.modelValue?.id ?? '',
  'inArray[campaign.region]': globalFilter?.value?.regions,
}));
const totalFilter = computed(() => {
  return { ...fixedFilter.value, ...filter.value };
});

const providerParams = reactive({
  sort: {
    sortKey,
    sortDirection,
  },
  filter: totalFilter,
  pagination: {
    page,
    perPage,
  },
});

type ColumnFilter = Partial<Filter> & Omit<Filter, 'key'>;
type ColumnDefinition = Column & { filter?: ColumnFilter | ColumnFilter[] } & { group: string };

const columns: ColumnDefinition[] = [
  {
    key: 'id',
    title: t('id'),
    sortable: true,
    group: t('pages.campaigns.surplusList.fieldGroups.general'),
    class: 'whitespace-nowrap',
    filter: {
      type: 'text',
      group: t('pages.campaigns.surplusList.filterGroups.general'),
      options: {
        label: t('id'),
      },
    },
  },
  {
    key: 'campaign.name',
    title: t('surplus.name'),
    group: t('pages.campaigns.surplusList.fieldGroups.general'),
    sortable: true,
    visible: true,
    filter: {
      key: 'campaign',
      type: 'dropdown',
      group: t('pages.campaigns.surplusList.fieldGroups.general'),
      options: {
        options: computed(() =>
          props.modelValue?.campaigns?.map((value) => ({
            label: value.name,
            value: value.id,
          }))
        ),
        label: t('surplus.name'),
      },
    },
  },
  {
    key: 'campaign.actualEndDate',
    title: t('surplus.actualEndDate'),
    sortable: true,
    visible: false,
    formatter: (v: string) => (v ? format(new Date(v), 'dd.MM.yyyy - HH:mm:ss') : '-'),
    group: t('pages.campaigns.surplusList.filterGroups.general'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.surplusList.filterGroups.general'),
      options: {
        label: t('surplus.actualEndDate'),
      },
    },
  },
  {
    key: 'campaign.region.name',
    title: t('campaign.region'),
    group: t('pages.campaigns.surplusList.fieldGroups.general'),
    sortable: true,
    visible: true,
  },
  {
    key: 'computedWeek',
    title: t('surplus.computedWeek'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.computedWeek'),
      },
    },
  },
  {
    key: 'totalDrivers',
    title: t('surplus.totalDrivers'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.totalDrivers'),
      },
    },
  },
  {
    key: 'driversWithFollowUpCampaign',
    title: t('surplus.driversWithFollowUpCampaign'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: false,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.driversWithFollowUpCampaign'),
      },
    },
  },
  {
    key: 'driversWithoutFollowUpCampaign',
    title: t('surplus.driversWithoutFollowUpCampaign'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.driversWithoutFollowUpCampaign'),
      },
    },
  },
  {
    key: 'noFollowUpPercentage',
    title: t('surplus.noFollowUpPercentage'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    unit: '%',
    formatter: (v: number) => `${Math.round(v)}%`,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.noFollowUpPercentage'),
      },
    },
  },
  {
    key: 'driversWithoutFollowUpCampaignApproximated',
    title: t('surplus.driversWithoutFollowUpCampaignApproximated'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.driversWithoutFollowUpCampaignApproximated'),
      },
    },
  },
  {
    key: 'noFollowUpPercentageApproximated',
    title: t('surplus.noFollowUpPercentageApproximated'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    unit: '%',
    formatter: (v: number) => `${Math.round(v)}%`,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.noFollowUpPercentageApproximated'),
      },
    },
  },
  {
    key: 'driversWithoutFollowUpAndNonCampaignTour',
    title: t('surplus.driversWithoutFollowUpAndNonCampaignTour'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.driversWithoutFollowUpAndNonCampaignTour'),
      },
    },
  },
  {
    key: 'noFollowUpWithTourPercentage',
    title: t('surplus.noFollowUpWithTourPercentage'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    visible: true,
    sortable: true,
    unit: '%',
    formatter: (v: number) => `${Math.round(v)}%`,
    filter: {
      type: 'range',
      group: t('pages.campaigns.surplusList.filterGroups.statistics'),
      options: {
        label: t('surplus.noFollowUpWithTourPercentage'),
      },
    },
  },
  {
    key: 'createdAt',
    title: t('surplus.createdAt'),
    group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
    sortable: true,
    visible: true,
    formatter: (v: string) => format(new Date(v), 'dd.MM.yyyy - HH:mm:ss'),
    filter: {
      type: 'datetime',
      group: t('pages.campaigns.surplusList.fieldGroups.statistics'),
      options: {
        label: t('surplus.createdAt'),
      },
    },
  },
];

const visibleColumns = useTableFields(
    columns,
);

const {
  data: items,
  loading,
  refetch,
} = useProvider<HydraCollection<StatisticPoint>>('/campaign_surpluses', providerParams, {
  disabled: campaignGroupNeedsLoading,
});

const records = computed(() => unref(items)?.['hydra:member']);
const total = computed(() => unref(items)?.['hydra:totalItems']);
const filters = computed(() =>
  columns.flatMap((col): Filter[] => {
    if (!col.filter) return [];
    if (Array.isArray(col.filter))
      return col.filter.map((f) => ({
        key: col.key,
        ...f,
      }));
    return [{ key: col.key, ...col.filter }];
  })
);

const isLoading = loading;

</script>

<style lang="scss" scoped>
button {
  @apply block;
}

.table {
  @apply flex flex-col;
}
</style>

